import { forwardRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Check,
  X,
  Pencil,
  Loader2,
  AlertTriangle,
  Send,
  ChevronDown,
} from "lucide-react";
import { Button } from "./Button";
import { LanguageBadge, type SupportedLanguage } from "./LanguageBadge";
import { RagSourceChip } from "./RagSourceChip";
import { cn } from "@/lib/cn";

/**
 * AiSuggestionCard — operator'ga AI tomonidan tayyorlangan javob taklifi.
 * Manba: docs/architecture/chatbot-ai-pipeline.md § 3.4 (chatbot-replier)
 *
 * Statelar:
 *   - PENDING (default) — Accept / Reject / Modify
 *   - ACCEPTED          — green check + fade
 *   - REJECTED          — fade-out
 *   - MODIFYING         — inline textarea
 */

export type AiSuggestionStatus =
  | "PENDING"
  | "ACCEPTED"
  | "REJECTED"
  | "EXPIRED";

export interface AiSuggestionSource {
  chunkId: string;
  documentId?: string;
  documentTitle?: string;
  title?: string;
  similarity?: number;
  page?: number | null;
}

export interface AiSuggestionCardProps {
  id: string;
  text: string;
  /** RAG manbalari (ko'rsatiladi sources sifatida) */
  sources?: AiSuggestionSource[];
  /** 0..1 ishonch darajasi (replier'dan) */
  confidence?: number;
  language?: SupportedLanguage | null;
  modelUsed?: string | null;
  createdAt?: string;
  status?: AiSuggestionStatus;
  /** Hallucination yoki past similarity warning */
  warning?: string | null;

  /** Aksiyalar */
  onAccept?: (id: string) => void | Promise<void>;
  onReject?: (id: string) => void | Promise<void>;
  onModify?: (id: string, newText: string) => void | Promise<void>;
  onSourceClick?: (source: AiSuggestionSource) => void;

  /** Mutating holat */
  busy?: boolean;
  className?: string;
}

const STATUS_CLS: Record<AiSuggestionStatus, string> = {
  PENDING: "border-[rgba(139,92,246,0.40)] bg-[rgba(139,92,246,0.06)]",
  ACCEPTED: "border-[rgba(16,185,129,0.45)] bg-[rgba(16,185,129,0.08)]",
  REJECTED: "border-[var(--border-subtle)] bg-[var(--bg-surface)] opacity-60",
  EXPIRED: "border-[var(--border-subtle)] bg-[var(--bg-surface)] opacity-50",
};

export const AiSuggestionCard = forwardRef<
  HTMLDivElement,
  AiSuggestionCardProps
>(function AiSuggestionCard(
  {
    id,
    text,
    sources = [],
    confidence,
    language,
    modelUsed,
    createdAt,
    status = "PENDING",
    warning,
    onAccept,
    onReject,
    onModify,
    onSourceClick,
    busy = false,
    className,
  },
  ref,
) {
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(text);
  const [showAllSources, setShowAllSources] = useState(false);

  // Sync draft when suggestion text changes externally
  useEffect(() => {
    if (!editMode) setDraft(text);
  }, [text, editMode]);

  const isPending = status === "PENDING";
  const visibleSources = showAllSources ? sources : sources.slice(0, 3);
  const remainingCount = sources.length - visibleSources.length;
  const confPct = confidence != null ? Math.round(confidence * 100) : null;
  const lowConfidence = confidence != null && confidence < 0.6;

  const handleAccept = async () => {
    if (!onAccept || busy) return;
    await onAccept(id);
  };

  const handleReject = async () => {
    if (!onReject || busy) return;
    await onReject(id);
  };

  const handleSendModified = async () => {
    if (!onModify || busy) return;
    const newText = draft.trim();
    if (!newText) return;
    await onModify(id, newText);
    setEditMode(false);
  };

  const handleCancelEdit = () => {
    setDraft(text);
    setEditMode(false);
  };

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, x: 24, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 24, scale: 0.96 }}
      transition={{ type: "spring", stiffness: 320, damping: 28 }}
      className={cn(
        "rounded-xl border p-3 transition-colors",
        STATUS_CLS[status],
        className,
      )}
      data-suggestion-id={id}
    >
      {/* Header */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--accent-violet)]">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          AI taklifi
        </div>
        <div className="flex items-center gap-1.5">
          {language && <LanguageBadge language={language} size="sm" />}
          {confPct != null && (
            <span
              className={cn(
                "rounded-md border px-1.5 py-0.5 font-mono text-[10px] leading-none",
                lowConfidence
                  ? "border-[var(--warning-border)] bg-[var(--warning-soft)] text-[var(--warning-fg)]"
                  : "border-[var(--border-subtle)] bg-[var(--bg-elevated)] text-[var(--text-secondary)]",
              )}
              title="Model ishonchlilik darajasi"
            >
              {confPct}%
            </span>
          )}
        </div>
      </div>

      {/* Warning banner */}
      {warning && (
        <div className="mb-2 flex items-start gap-1.5 rounded-md border border-[var(--warning-border)] bg-[var(--warning-soft)] px-2 py-1.5 text-[11px] text-[var(--warning-fg)]">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{warning}</span>
        </div>
      )}

      {/* Body */}
      {editMode ? (
        <div className="mb-2 space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            autoFocus
            className={cn(
              "w-full rounded-md border bg-[var(--bg-base)] px-2.5 py-2 text-sm text-[var(--text-primary)]",
              "border-[var(--border-strong)] focus:border-[var(--accent-violet)] focus:outline-none",
              "resize-none",
            )}
            placeholder="Tahrirlangan javobni yozing…"
          />
        </div>
      ) : (
        <p className="mb-2 whitespace-pre-wrap text-sm text-[var(--text-primary)]">
          {text}
        </p>
      )}

      {/* Sources */}
      {sources.length > 0 && !editMode && (
        <div className="mb-2.5 flex flex-wrap items-center gap-1">
          {visibleSources.map((src) => (
            <RagSourceChip
              key={src.chunkId}
              size="sm"
              title={src.title || src.documentTitle || src.chunkId.slice(0, 8)}
              documentTitle={src.documentTitle}
              page={src.page}
              similarity={src.similarity}
              onClick={onSourceClick ? () => onSourceClick(src) : undefined}
            />
          ))}
          {remainingCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAllSources(true)}
              className="inline-flex h-5 items-center gap-0.5 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-1.5 text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <ChevronDown className="h-3 w-3" aria-hidden />+{remainingCount}
            </button>
          )}
        </div>
      )}

      {/* Footer / Actions */}
      <AnimatePresence mode="wait" initial={false}>
        {isPending && (
          <motion.div
            key="actions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center justify-between gap-2"
          >
            {editMode ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={busy}
                >
                  Bekor qilish
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSendModified}
                  disabled={busy || !draft.trim()}
                >
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <Send className="h-3.5 w-3.5" aria-hidden />
                  )}
                  Yuborish
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleReject}
                    disabled={busy}
                    title="Rad etish"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                    Rad etish
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditMode(true)}
                    disabled={busy}
                    title="Tahrirlash"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                    Tahrirlash
                  </Button>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAccept}
                  disabled={busy}
                  title="Qabul qilish va yuborish"
                >
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <Check className="h-3.5 w-3.5" aria-hidden />
                  )}
                  Qabul qilish
                </Button>
              </>
            )}
          </motion.div>
        )}

        {status === "ACCEPTED" && (
          <motion.div
            key="accepted"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--success-fg)]"
          >
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 20 }}
              className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-[var(--success-soft)]"
            >
              <Check className="h-3 w-3" aria-hidden />
            </motion.span>
            Qabul qilindi va yuborildi
          </motion.div>
        )}

        {status === "REJECTED" && (
          <motion.div
            key="rejected"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)]"
          >
            <X className="h-3 w-3" aria-hidden />
            Rad etildi
          </motion.div>
        )}

        {status === "EXPIRED" && (
          <motion.div
            key="expired"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)]"
          >
            Muddati tugagan
          </motion.div>
        )}
      </AnimatePresence>

      {/* Meta footer */}
      {(modelUsed || createdAt) && status === "PENDING" && !editMode && (
        <div className="mt-2 flex items-center justify-between border-t border-[var(--border-subtle)] pt-1.5 text-[10px] text-[var(--text-muted)]">
          {modelUsed && <span className="font-mono truncate">{modelUsed}</span>}
          {createdAt && (
            <time dateTime={createdAt}>
              {new Date(createdAt).toLocaleTimeString("uz-UZ", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </time>
          )}
        </div>
      )}
    </motion.div>
  );
});
